import { useForm } from '@tanstack/react-form'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import { ErrorBanner } from '@workspace/ui/components/error-banner'
import { FormField } from '@workspace/ui/components/form-field'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { Switch } from '@workspace/ui/components/switch'
import { TextInput } from '@workspace/ui/components/text-input'
import { Textarea } from '@workspace/ui/components/textarea'
import { Check, Eye, FileText, Trash2, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { DocumentUpload } from '../../../components/document-upload'
import {
  useCurrentUserQuery,
  useInvestorQuery,
  useLinkFounderResumeMutation,
  useUnlinkFounderResumeMutation,
  useUpdateMeMutation,
} from '../../../hooks/api'
import { useImageUpload } from '../../../hooks/use-image-upload'
import { useIsFounder, useIsInvestor } from '../../../hooks/use-user-type'
import { DocumentPreviewModal, type PreviewDocument } from '../../preview'

type ProfileEditProps = {
  onExitEdit: () => void
}

export function ProfileEdit({ onExitEdit }: ProfileEditProps) {
  const { data: userResponse, isLoading, error } = useCurrentUserQuery()
  const user = userResponse?.data
  const isFounder = useIsFounder()
  const isInvestor = useIsInvestor()

  const { data: investorResponse } = useInvestorQuery({ enabled: isInvestor })

  const resumeDocument = user?.resumeDocument
  const investor = investorResponse?.data

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)
  const [coverImageUrl, setCoverImageUrl] = useState<string | null>(null)
  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)

  const updateUserMut = useUpdateMeMutation()
  const linkResumeMut = useLinkFounderResumeMutation()
  const unlinkResumeMut = useUnlinkFounderResumeMutation()

  const avatarUpload = useImageUpload({
    onSuccess: (url) => setAvatarUrl(url),
  })

  const coverUpload = useImageUpload({
    onSuccess: (url) => setCoverImageUrl(url),
  })

  const handleResumeUploadComplete = (documentId: string) => {
    linkResumeMut.mutate({ body: { documentId } })
  }

  const handleResumeDelete = () => {
    unlinkResumeMut.mutate({})
  }

  const getFormValues = (data?: typeof user) => ({
    name: data?.name ?? '',
    bio: data?.bio ?? '',
    location: data?.location ?? '',
    websiteUrl: data?.websiteUrl ?? '',
    linkedinUrl: data?.linkedinUrl ?? '',
    twitterUrl: data?.twitterUrl ?? '',
    githubUrl: data?.githubUrl ?? '',
    crunchbaseUrl: data?.crunchbaseUrl ?? '',
    emailSubscribed: data?.emailSubscribed ?? true,
  })

  const form = useForm({
    defaultValues: getFormValues(user),
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: getFormValues is a stable factory, user?.id guards re-runs
  useEffect(() => {
    if (!user) return
    setAvatarUrl(user.image ?? null)
    setCoverImageUrl(user.coverImage ?? null)
    form.reset(getFormValues(user))
  }, [user?.id, form.reset, user])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="flex items-center justify-center py-20">
        <ErrorBanner message={error?.message || 'Failed to load profile'} />
      </div>
    )
  }

  const handleCancel = () => {
    form.reset(getFormValues(user))
    setAvatarUrl(user.image ?? null)
    setCoverImageUrl(user.coverImage ?? null)
    onExitEdit()
  }

  const handleSave = async () => {
    const values = form.state.values

    updateUserMut.mutate(
      {
        body: {
          name: values.name || null,
          image: avatarUrl,
          coverImage: coverImageUrl,
          bio: values.bio || null,
          location: values.location || null,
          websiteUrl: values.websiteUrl || null,
          linkedinUrl: values.linkedinUrl || null,
          twitterUrl: values.twitterUrl || null,
          githubUrl: values.githubUrl || null,
          crunchbaseUrl: values.crunchbaseUrl || null,
          emailSubscribed: values.emailSubscribed,
        },
      },
      {
        onSuccess: () => onExitEdit(),
      }
    )
  }

  // Compute subtitle based on role
  const getSubtitle = () => {
    if (isInvestor && investor) {
      return investor.name ?? user?.location
    }
    return user?.location
  }

  const isSaving = updateUserMut.isPending

  return (
    <>
      <ProfileHeader
        avatarUrl={avatarUrl ?? user.image}
        coverImageUrl={coverImageUrl ?? user.coverImage}
        name={user.name ?? user.email}
        subtitle={getSubtitle()}
        isEditMode
        onAvatarChange={(file) => avatarUpload.upload(file)}
        onCoverImageChange={(file) => coverUpload.upload(file)}
        isAvatarUploading={avatarUpload.isUploading}
        isCoverUploading={coverUpload.isUploading}
        badges={
          <Badge variant="outline-primary" size="md">
            {isFounder ? 'Founder' : 'Investor'}
          </Badge>
        }
        actions={
          <>
            <Button variant="outline" size="sm" onClick={handleCancel}>
              <X className="size-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isSaving}>
              <Check className="size-4" />
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <form.Field name="name">
            {(field) => (
              <FormField label="Name">
                <TextInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="Your name"
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name="location">
            {(field) => (
              <FormField label="Location">
                <TextInput
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g., San Francisco, CA"
                />
              </FormField>
            )}
          </form.Field>
        </div>

        <form.Field name="bio">
          {(field) => (
            <FormField label="Bio">
              <Textarea
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder="Tell us about yourself..."
                rows={4}
              />
            </FormField>
          )}
        </form.Field>

        {isFounder && (
          <ProfileSection title="Resume">
            {resumeDocument ? (
              <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                <div className="flex items-center gap-3">
                  <FileText className="size-8 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm">{resumeDocument.fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      {resumeDocument.fileSize
                        ? `${Math.round(resumeDocument.fileSize / 1024)} KB`
                        : 'PDF'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPreviewDocument({
                        id: resumeDocument.id,
                        fileName: resumeDocument.fileName,
                        mimeType: resumeDocument.mimeType,
                      })
                    }
                  >
                    <Eye className="size-4" />
                    Preview
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleResumeDelete}
                    disabled={unlinkResumeMut.isPending}
                  >
                    <Trash2 className="size-4" />
                    {unlinkResumeMut.isPending ? 'Removing...' : 'Remove'}
                  </Button>
                </div>
              </div>
            ) : (
              <DocumentUpload
                onUploadComplete={handleResumeUploadComplete}
                accept={{ 'application/pdf': ['.pdf'] }}
                labels={{ singular: 'resume (PDF)' }}
              />
            )}
          </ProfileSection>
        )}

        <ProfileSection title="Social">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <form.Field name="websiteUrl">
              {(field) => (
                <FormField label="Website">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://yourwebsite.com"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="linkedinUrl">
              {(field) => (
                <FormField label="LinkedIn">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://linkedin.com/in/username"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="twitterUrl">
              {(field) => (
                <FormField label="Twitter">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://twitter.com/username"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="githubUrl">
              {(field) => (
                <FormField label="GitHub">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://github.com/username"
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name="crunchbaseUrl">
              {(field) => (
                <FormField label="Crunchbase">
                  <TextInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder="https://crunchbase.com/person/name"
                  />
                </FormField>
              )}
            </form.Field>
          </div>
        </ProfileSection>

        <ProfileSection title="Email Preferences">
          <form.Field name="emailSubscribed">
            {(field) => (
              <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
                <div>
                  <p className="font-medium text-sm text-white">Automated emails</p>
                  <p className="text-xs text-muted-foreground">
                    Receive onboarding reminders and activity notifications
                  </p>
                </div>
                <Switch
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked)}
                />
              </div>
            )}
          </form.Field>
        </ProfileSection>
      </div>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
    </>
  )
}
